/**
 * The AI helpers of the Map and Transform screens, as the browser sees them.
 * Mirrors packages/api/src/services/data-mapper/data-plane/llm-mapping.ts and
 * data-mapper-llm-mapping.ts: what the API answers, what it accepts, and the
 * sentences its refusals become.
 */
import { loadErrorMessage } from "shared/loadOrError";
import { refusalReason } from "../dataMapper.types";
import type { ParsedField } from "../engine/types";

/** At most this many source tables per suggest call (MAX_SUGGEST_TABLES). */
export const MAX_SUGGEST_TABLES = 25;
/** At most this many target fields per suggest call (MAX_TARGET_FIELDS). */
export const MAX_SUGGEST_FIELDS = 500;
/** At most this many example pairs per regex call, each this long (MAX_REGEX_EXAMPLES, MAX_SAMPLE_LENGTH). */
export const MAX_REGEX_EXAMPLES = 20;
export const MAX_EXAMPLE_LENGTH = 500;

/** One suggestion as the API answers it: every name already checked against the real ones. */
export interface MappingSuggestion {
  sourceTable: string;
  sourceColumn: string;
  targetField: string;
  confidence: number;
  reasoning: string;
}

export interface SuggestMappingsAnswer {
  suggestions: MappingSuggestion[];
  /** Suggestions the API threw away: malformed, or naming a column or field that does not exist. */
  dropped: number;
  droppedReasons?: { malformed: number; unknownSourceColumn: number; unknownTargetField: number };
  belowThreshold?: number;
  duplicates?: number;
  /** AI calls (of several) whose answer held no list of mappings at all. */
  unreadableBatches?: number;
  sampleValuesIncluded?: boolean;
}

export interface RegexExample {
  input: string;
  output: string;
}

export interface RegexSuggestion {
  pattern: string;
  replacement: string;
  flags: string;
  explanation: string;
  matched: number;
  total: number;
  results: Array<{ input: string; expected: string; actual: string }>;
  warning?: string;
}

/** Said in place of a suggestion when HQ's LLM router is not wired for these tasks. */
export const LLM_SUGGEST_UNAVAILABLE_MESSAGE =
  "AI suggestions are not available: HQ's LLM router is not set up for them here. " +
  "An administrator can route the data-mapper tasks (suggest field mappings, suggest a regex from examples) " +
  "to a provider under AI → Task Routing.";

/** A failed suggest call as the sentence the screen shows. */
export function suggestErrorMessage(err: unknown, fallback: string): string {
  if (refusalReason(err) === "llm-unavailable") return LLM_SUGGEST_UNAVAILABLE_MESSAGE;
  return loadErrorMessage(err, fallback);
}

/** What the screen says after suggestions arrived. */
export function suggestNotice(answer: SuggestMappingsAnswer, added: number): string {
  const parts = [
    added === 0
      ? "The AI found no mapping for the unmapped fields."
      : `${added} suggested mapping${added === 1 ? "" : "s"} added. Accept or reject each one, then save.`,
  ];
  if (answer.dropped > 0) {
    const r = answer.droppedReasons;
    const invented = r ? r.unknownSourceColumn + r.unknownTargetField : answer.dropped;
    const malformed = r ? r.malformed : 0;
    const why = [
      invented > 0 ? `${invented} named a column or field that does not exist` : null,
      malformed > 0 ? `${malformed} ${malformed === 1 ? "was" : "were"} not a valid suggestion` : null,
    ].filter(Boolean);
    parts.push(`${answer.dropped} suggestion${answer.dropped === 1 ? " was" : "s were"} dropped (${why.join("; ")}).`);
  }
  if (answer.unreadableBatches && answer.unreadableBatches > 0) {
    parts.push(
      `${answer.unreadableBatches} of the AI's answers could not be read, so some columns got no suggestion; try again for the fields still unmapped.`
    );
  }
  if (answer.sampleValuesIncluded) parts.push("A few sample values were sent with the column names (an administrator turned that on).");
  return parts.join(" ");
}

const clip = (s: string | undefined, max: number): string | undefined =>
  s === undefined ? undefined : s.length > max ? s.slice(0, max) : s;

/** A target field as the API accepts it: only what a prompt uses. */
export function targetFieldPayload(f: ParsedField): Record<string, unknown> {
  return {
    name: f.name,
    type: String(f.type).slice(0, 60),
    isOptional: f.isOptional,
    isNullable: f.isNullable,
    ...(f.format ? { format: clip(f.format, 60) } : {}),
    ...(f.description ? { description: clip(f.description, 1000) } : {}),
  };
}

/**
 * The helper's text as example pairs: one per line, `input => output` (the
 * single space either side of `=>` is dropped, every other character kept).
 * Returns the pairs, or the reason they cannot be sent.
 */
export function parseRegexExamples(text: string): { examples: RegexExample[]; problem: string | null } {
  const examples: RegexExample[] = [];
  for (const raw of text.split("\n")) {
    if (!raw.trim()) continue;
    const at = raw.indexOf("=>");
    if (at < 0) return { examples, problem: `Write each example as "input => output" (this line has no =>: ${raw.trim().slice(0, 40)}).` };
    // One space either side of "=>" is layout; any other whitespace is part of the example.
    const input = raw.slice(0, at).replace(/ $/, "");
    const output = raw.slice(at + 2).replace(/^ /, "").replace(/\r$/, "");
    if (input.length > MAX_EXAMPLE_LENGTH || output.length > MAX_EXAMPLE_LENGTH) {
      return { examples, problem: `An example is longer than ${MAX_EXAMPLE_LENGTH} characters.` };
    }
    examples.push({ input, output });
  }
  if (examples.length === 0) return { examples, problem: "Give at least one example." };
  if (examples.length > MAX_REGEX_EXAMPLES) return { examples, problem: `Give at most ${MAX_REGEX_EXAMPLES} examples.` };
  return { examples, problem: null };
}
